import { useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ProcedureContext } from "@/context/ProcedureContext";
import { Checklist, Delete } from "@mui/icons-material";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { Translate } from "@/components/Application/Translate";
import { tokens } from "@/components/Application/theme";
import {
	useCreateChecklistStep,
	useDeleteChecklistStep,
} from "@/components/Procedures/API/ChecklistMutations";
import { useEditorContent } from "@/components/Procedures/API/ChecklistQueries";
import { useSaveChecklistStep } from "@/components/Procedures/API/ManualChecklistCacheMutations";
import {
	CheckpointType,
	CreateChecklistStepRequest,
	DeleteChecklistStepRequest,
} from "@/components/Procedures/Types/ManualChecklistTypes";

import { ErrorToast, SuccessToast } from "@/helpers/alerts/AlertToast";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

import { UpsertProcedureNodeSteps } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

export default function ManualChecklistEditorActions({
	activeChecklistId,
	sx,
}: {
	activeChecklistId: number;
	sx?: any;
}) {
	const { id, schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { data: editorContent } = useEditorContent(+activeChecklistId);
	const { mutateAsync: saveStepPreview } = useSaveChecklistStep();
	const saveChecklist = UpsertProcedureNodeSteps();
	const addChecklistStep = useCreateChecklistStep();
	const deleteChecklistStep = useDeleteChecklistStep();

	const { activeStep, setActiveStep } = useContext(ProcedureContext);

	const navigate = useNavigate();

	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const addStep = async () => {
		const newStep = new ProcedureNodeStep();
		newStep.ChecklistType = "Check";
		newStep.StepId = editorContent?.Steps?.length
			? editorContent.Steps.length + 1
			: 0;

		const request: CreateChecklistStepRequest = {
			ProcedureId: +id!,
			ChecklistId: activeChecklistId,
			StepId: newStep.StepId,
			CheckpointType: CheckpointType.Checkpoint,
		};

		addChecklistStep(request);

		saveStepPreview(newStep);
		setActiveStep(activeStep + 1);
	};

	const deleteStep = async () => {
		try {
			const currentStepId = +editorContent?.Steps[activeStep]?.StepId;
			const request: DeleteChecklistStepRequest = {
				StepId: currentStepId,
				ChecklistId: checklistId,
			};
			if (currentStepId) {
				deleteChecklistStep(request);
			}
			setActiveStep(activeStep - 1);
			SuccessToast("Step deleted successfully");
		} catch (error) {
			ErrorToast("Error deleting step");
		}
	};

	const isLastStep = useMemo(
		() =>
			!editorContent?.Steps || activeStep === editorContent?.Steps?.length - 1,
		[activeStep, editorContent?.Steps],
	);

	const handleNext = () => {
		if (isLastStep) {
			navigate(`/procedure/${id}/checklist/${activeChecklistId}`);
			return;
		}
		setActiveStep(activeStep + 1);
	};

	const handleBack = () => {
		setActiveStep(activeStep - 1);
	};

	return (
		<Box sx={{ width: "100%", ...sx }}>
			<Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
				<Button
					color="inherit"
					variant="outlined"
					disabled={activeStep === -1}
					onClick={handleBack}
					sx={{ mr: 1, borderColor: colors.yellowAccent[300] }}
				>
					<Translate String="Manual.Checklist.Previous.Step">Back</Translate>
				</Button>
				{activeStep > -1 && (
					<Button
						color="error"
						variant="outlined"
						onClick={deleteStep}
						startIcon={<Delete />}
					>
						<Translate String="Manual.Checklist.Delete.Step">
							Delete step
						</Translate>
					</Button>
				)}
				<Box sx={{ flex: "1 1 auto" }} />
				{isLastStep && (
					<Button onClick={addStep}>
						<Translate String="Manual.Checklist.Add.Step">Add step</Translate>
					</Button>
				)}
				<Button
					variant="contained"
					sx={{
						backgroundColor: colors.yellowAccent[300],
						"&:hover": { backgroundColor: colors.yellowAccent[400] },
					}}
					onClick={handleNext}
				>
					{isLastStep ? (
						<Translate String="Manual.Checklist.Finish">Finish</Translate>
					) : (
						<Translate String="Manual.Checklist.Next.Step">Next</Translate>
					)}
				</Button>
			</Box>
		</Box>
	);
}
